import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Workflows.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { formatAppDate } from '../../utils/dateUtils';

const WorkflowsList = forwardRef(({ onSelectWorkflow, selectedWorkflowId, onNew }, ref) => {
  const { t } = useTranslation('production');
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchWorkflows
  }));

  useEffect(() => {
    fetchWorkflows();
  }, [statusFilter]);

  const fetchWorkflows = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/workflows/');
      if (statusFilter === 'active') {
        url += '?is_active=true';
      } else if (statusFilter === 'inactive') {
        url += '?is_active=false';
      }

      const response = await apiFetch(url);
      if (response.ok) {
        const data = await response.json();
        setWorkflows(data.workflows || data || []);
      } else {
        setError(t('workflows.list.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching workflows:', error);
      setError(t('workflows.list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = workflow.workflow_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (workflow.workflow_description && workflow.workflow_description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge status-active">{t('workflows.list.filters.active')}</span>
    ) : (
      <span className="status-badge status-inactive">{t('workflows.list.filters.inactive')}</span>
    );
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && workflows.length === 0) {
    return <div className="list-container"><div className="loading">{t('workflows.list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('workflows.list.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('workflows.list.newWorkflow')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('workflows.list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('workflows.list.filters.allStatus')}</option>
            <option value="active">{t('workflows.list.filters.active')}</option>
            <option value="inactive">{t('workflows.list.filters.inactive')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('workflows.list.headers.workflowName')}</th>
              <th>{t('workflows.list.headers.description')}</th>
              <th>{t('workflows.list.headers.status')}</th>
              <th>{t('workflows.list.headers.createdDate')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkflows.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">
                  {searchTerm ? t('workflows.list.noData.noResults') : t('workflows.list.noData.noWorkflows')}
                </td>
              </tr>
            ) : (
              filteredWorkflows.map(workflow => (
                <tr
                  key={workflow.workflow_id}
                  onClick={() => onSelectWorkflow(workflow.workflow_id)}
                  className={selectedWorkflowId === workflow.workflow_id ? 'selected' : ''}
                >
                  <td className="template-name">
                    <strong>{workflow.workflow_name}</strong>
                  </td>
                  <td className="description">
                    {workflow.workflow_description || t('workflows.list.defaultValues.noDescription')}
                  </td>
                  <td>
                    {getStatusBadge(workflow.is_active)}
                  </td>
                  <td className="date">
                    {formatAppDate(workflow.created_at || workflow.created_date)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('workflows.list.footer.recordCount', { filtered: filteredWorkflows.length, total: workflows.length })}
        </div>
      </div>
    </div>
  );
});

WorkflowsList.displayName = 'WorkflowsList';

export default WorkflowsList;